/**
 * The server's clock, learned from the server.
 *
 * A running job's duration is `now − created_at`, and `created_at` comes from
 * the server. Subtracting it from the *browser's* clock is what produced "0s
 * for a long time" on a live upload: the two clocks were ~24 s apart, the
 * difference went negative, and Math.max(0, …) pinned it to zero. CLAUDE.md
 * records the same arithmetic in the other direction showing a 15-minute
 * upload as 2h 15m.
 *
 * **The offset must come from a timestamp the server produced just now, and
 * nothing else.** It used to be read off a running job's `updated_at`, on the
 * documented assumption that the streaming upload loop rewrites it every couple
 * of seconds. That assumption held for uploads and for nothing else: results
 * packaging never wrote to its job row at all, so its `updated_at` was frozen
 * at creation — and because this offset is module state shared by the whole
 * page, one packaging row made *every* job read "0s", healthy uploads included.
 *
 * So the reading now comes from the HTTP `Date` header of any API response,
 * which is by definition "the server, just now" and cannot be a stale row.
 * Whole-second granularity is finer than the durations displayed.
 */

// How far the server is from this browser, in ms (server − browser). 0 until a
// response arrives, which is the old first-paint behaviour and so cannot
// regress it.
let serverOffsetMs = 0;

/** Test seam — the offset is module state and would leak between cases. */
export function __setServerOffset(ms) { serverOffsetMs = ms; }

/**
 * Learn the offset from a timestamp the server produced as it answered.
 *
 * Anything unparseable leaves the offset alone: a missing or malformed `Date`
 * must not throw away a good reading, and must not throw at all — this is
 * called on the response path of every request.
 */
export function noteServerTime(value) {
  if (!value) return;
  const t = Date.parse(value);
  if (!Number.isNaN(t)) serverOffsetMs = t - Date.now();
}

/** Now, on the server's clock rather than this machine's. */
export function serverNow() { return Date.now() + serverOffsetMs; }
